import { useState, useEffect, useRef } from 'react';

/**
 * Custom Hook: useDataLoadingState
 * Manages loading state with delay to prevent UI flickering
 *
 * Improved Logic:
 * - Never show empty state until loading has occurred at least once
 * - After delay, show loading spinner if data still loading
 * - Once data arrives, show data
 * - If no data after loading has completed, show empty state
 *
 * This prevents the "empty state first, then spinner" issue by tracking
 * whether loading has ever been true.
 */
export const useDataLoadingState = (isLoading, hasData, delayMs = 100) => {
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const hasLoadingStartedRef = useRef(false);
  const timeoutRef = useRef(null);

  // Track if loading has ever been true
  useEffect(() => {
    if (isLoading) {
      hasLoadingStartedRef.current = true;
    }
  }, [isLoading]);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isLoading) {
      // Set timeout to show loading spinner after delay
      timeoutRef.current = setTimeout(() => {
        setShowLoadingSpinner(true);
      }, delayMs);
    } else {
      // Data finished loading, hide spinner immediately
      setShowLoadingSpinner(false);
    }

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, delayMs]);

  return {
    showLoadingSpinner,
    // Only show empty state if loading has occurred AND data is empty
    showEmptyState: !isLoading && !hasData && hasLoadingStartedRef.current,
    showData: !isLoading && hasData,
    isLoading,
  };
};

export default useDataLoadingState;